import { ActionTree } from "vuex";
import { RootState } from "@/store/modules/root/interfaces";
import {
  ai_chat,
  ai_create_session,
  fetch_exam_data,
  ChatPayload,
  ChatResponse
} from "@/store/apis/ai_assessment";
import {
  create_assessment,
  create_assessment_questions
} from "@/store/apis/recruiter";
import { AIAssessmentState } from "./interfaces";

const actions: ActionTree<AIAssessmentState, RootState> = {
  async create_session({ commit }) {
    commit("SET_LOADING_CONTEXT", "creating_session");
    commit("SET_ERROR", null);
    try {
      const { data } = await ai_create_session();
      commit("SET_SESSION_ID", data.session_id);
      return data;
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Failed to create session";
      commit("SET_ERROR", errorMessage);
    } finally {
      commit("SET_LOADING_CONTEXT", null);
    }
  },
  async recreate_session({ commit }) {
    // Silently recreate session without setting loading context
    commit("SET_ERROR", null);
    try {
      const { data } = await ai_create_session();
      commit("SET_SESSION_ID", data.session_id);
      return data;
    } catch (error: unknown) {
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Failed to recreate session";
      commit("SET_ERROR", errorMessage);
      throw error;
    }
  },
  async chat({ state, commit }, message: string) {
    if (!state.sessionId) return;
    const payload: ChatPayload = { session_id: state.sessionId, message };

    return new Promise((resolve, reject) => {
      let isGeneratingAssessment = false;
      let chunk_str = "";
      ai_chat(
        payload,
        // onMessage callback - called for each streaming chunk
        (chunk: string) => {
          chunk_str += chunk;
          // Check if the message indicates assessment generation
          if (
            chunk_str.toLowerCase().includes("generating") ||
            chunk_str.toLowerCase().includes("assessment_type") ||
            chunk_str.toLowerCase().includes("assessment questions")
          ) {
            if (!isGeneratingAssessment) {
              isGeneratingAssessment = true;
              commit("SET_LOADING_CONTEXT", "creating_assessment");
              commit("REMOVE_LAST_MESSAGE");
            }
          } else if (!isGeneratingAssessment) {
            // Update the last AI message in the chat log directly
            commit("UPDATE_LAST_AI_MESSAGE", chunk);
          }

          commit("SET_LOADING", false);
        },
        // onComplete callback - called when streaming is complete
        async (data: ChatResponse) => {
          try {
            // If we were generating an assessment, fetch the complete exam data
            if (isGeneratingAssessment && state.sessionId) {
              const examData = await fetch_exam_data(state.sessionId);
              if (examData) {
                commit("SET_ASSESSMENT_DATA", examData);
                // Show success message instead of raw data
                commit(
                  "UPDATE_LAST_AI_MESSAGE",
                  "✅ Assessment generated successfully!"
                );
              }
            } else if (data.assessment_data) {
              commit("SET_ASSESSMENT_DATA", data.assessment_data);
            }
          } catch (error) {
            console.error("Error fetching exam data:", error);
            // Fallback to original data if API call fails
            if (data.assessment_data) {
              commit("SET_ASSESSMENT_DATA", data.assessment_data);
            }
          }

          commit("SET_LOADING", false);
          commit("SET_LOADING_CONTEXT", null);
          resolve(data);
        },
        // onError callback
        (error: Error) => {
          commit("SET_ERROR", error?.message || "Chat failed");
          commit("SET_LOADING", false);
          commit("SET_LOADING_CONTEXT", null);
          reject(error);
        }
      ).catch((error: Error) => {
        commit("SET_ERROR", error?.message || "Chat failed");
        commit("SET_LOADING", false);
        commit("SET_LOADING_CONTEXT", null);
        reject(error);
      });
    });
  },
  reset({ commit }) {
    commit("RESET");
  },

  async reset_assessment_data({ commit }) {
    commit("RESET");
  },
  async startEditMode({ commit, dispatch, state }) {
    commit("SET_EDIT_MODE", true);

    // Create new AI session when starting edit mode
    try {
      // Get current assessment data from the store
      const assessmentData = state.assessmentData;

      const payload = {
        id: 0,
        assessment_type: assessmentData?.assessment_type || "custom",
        assessment_name: assessmentData?.topic || "Assessment",
        difficulty: assessmentData?.difficulty || "easy",
        is_active: true,
        created_at: new Date().toISOString(),
        total_questions: assessmentData?.total_questions?.toString() || "0",
        questions: assessmentData?.questions || []
      };

      const result = await dispatch(
        "recruiter/CREATE_AI_ASSESSMENT_SESSION",
        payload,
        { root: true }
      );

      if (result && result.session_id) {
        // Set the session ID in AI assessment store
        commit("SET_SESSION_ID", result.session_id);
        commit("APPEND_CHAT", {
          type: "ai",
          text: "I'm ready to help you edit your assessment. You can ask me to modify questions, add new ones, or improve the assessment structure."
        });
      }
    } catch (error) {
      console.error("Failed to create edit session:", error);
      commit("APPEND_CHAT", {
        type: "ai",
        text: "Sorry, I couldn't create an edit session. Please try again."
      });
    }
  },
  finishEditMode({ commit }) {
    commit("SET_EDIT_MODE", false);
    commit("SET_EDITING_QUESTION_INDEX", null);
  },
  startEditingQuestion({ commit }, index: number) {
    commit("SET_EDITING_QUESTION_INDEX", index);
  },
  saveQuestion({ commit }, payload: { index: number; question: unknown }) {
    commit("UPDATE_QUESTION", payload);
    commit("SET_EDITING_QUESTION_INDEX", null);
  },
  async finalize_assessment({ state, commit, rootGetters, dispatch }) {
    try {
      // Step 1: Create the assessment

      // Get current user from auth state
      const currentUser = rootGetters["auth/GET_USER_DETAILS"];
      const userId = currentUser?.id;
      const assessmentPayload = {
        assessment_name: state.assessmentData?.topic || "",
        assessment_type: state.assessmentData?.assessment_type || "",
        difficulty: state.assessmentData?.difficulty || "",
        generated_from: "AI",
        created_by: userId
      };

      const assessmentResponse = await create_assessment(assessmentPayload);
      const assessmentId =
        assessmentResponse.data.data.id ||
        assessmentResponse.data.data.assessment_id;
      // Step 2: Transform and save the questions

      await dispatch(
        "recruiter/ASSIGN_ASSESSMENT_TO_APPLICANTS",
        { assessment_id: assessmentId },
        { root: true }
      );

      if (
        state.assessmentData?.questions &&
        state.assessmentData.questions.length > 0
      ) {
        const questionsPayload = {
          questions: state.assessmentData.questions.map((question: unknown) => {
            const q = question as {
              text?: string;
              question?: string;
              type?: string;
              options?: string[];
              correct_answer?: string;
              answer?: string;
            };
            return {
              question_text: q.text || q.question || "",
              question_type: q.type || "short_answer",
              is_question_enable: true,
              created_by: userId,
              question_options: (q.options || []) as string[],
              correct_answer: q.correct_answer || q.answer || "",
              question_time_allowed: 60, // Default time, can be made configurable
              user_id: undefined, // Optional
              job_id: undefined // Optional
            };
          })
        };
        await create_assessment_questions(assessmentId, questionsPayload);
      }

      // Step 4: Return the complete data
      return {
        assessmentData: state.assessmentData,
        savedAssessmentId: assessmentId,
        success: true
      };
    } catch (error: unknown) {
      console.error("Error finalizing assessment:", error);
      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || "Failed to save assessment";
      commit("SET_ERROR", errorMessage);
      return {
        assessmentData: state.assessmentData,
        success: false,
        error: errorMessage
      };
    }
  }
};

export default actions;
